import { useCallback, useEffect, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { APP_LOCALE, formatDate } from "../lib/dates";
import { Avatar } from "./Avatar";
import { AlbumActivityCard } from "./AlbumActivityCard";
import { MediaTile } from "./MediaTile";
import { PostMediaStories } from "./MediaStory";
import { PostReactions } from "./PostReactions";
import { PostComments } from "./PostComments";
import { useMe } from "../hooks/useMe";
import {
  can,
  eventDateInputValue,
  formatEventDate as formatPicturedDate,
} from "@family/shared";
import type {
  AlbumActivity,
  AlbumActivityResponse,
  Post,
  PostsListResponse,
  Role,
} from "@family/shared";

interface TimelineProps {
  familySlug: string;
  /** Optional pictured-year filter; only posts whose eventDate falls in this
   *  calendar year are shown. */
  picturedYear?: number | null;
  /** "recent" lists newest-added first as one flat feed; "year" (default)
   *  is the historical timeline, grouped under pictured-year headings. */
  sort?: TimelineSort;
}

export type TimelineSort = "recent" | "year";

function yearOf(post: Post): number {
  const date = post.eventDate ?? post.createdAt;
  return new Date(date).getUTCFullYear();
}

function PostSkeleton() {
  return (
    <div className="card" style={{ marginBottom: "var(--s-3)" }}>
      <div style={{ height: 16, width: "60%", background: "var(--c-bg-alt)", borderRadius: 4, marginBottom: 12 }} />
      <div style={{ height: 14, width: "100%", background: "var(--c-bg-alt)", borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 14, width: "80%", background: "var(--c-bg-alt)", borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 200, width: "100%", background: "var(--c-bg-alt)", borderRadius: 8, marginTop: 12 }} />
      <div style={{ display: "flex", gap: 12, marginTop: 12, paddingTop: 8, borderTop: "1px solid var(--c-border)" }}>
        <div style={{ height: 12, width: 100, background: "var(--c-bg-alt)", borderRadius: 4 }} />
        <div style={{ height: 12, width: 60, background: "var(--c-bg-alt)", borderRadius: 4 }} />
      </div>
    </div>
  );
}

export function Timeline({ familySlug, picturedYear, sort = "year" }: TimelineProps) {
  const meQuery = useMe();
  const queryClient = useQueryClient();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const query = useInfiniteQuery<PostsListResponse>({
    queryKey: ["posts", familySlug, picturedYear ?? null, sort],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set("cursor", pageParam as string);
      if (picturedYear) params.set("pictured", String(picturedYear));
      if (sort === "recent") params.set("sort", "recent");
      const qs = params.toString();
      const url = qs
        ? `/families/${familySlug}/posts?${qs}`
        : `/families/${familySlug}/posts`;
      return apiFetch<PostsListResponse>(url);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // "Photos added to an album" cards, woven into the Newest feed by the time
  // they were added. Not in By year (they have no single pictured year) or
  // while a year filter is on.
  const showActivity = sort === "recent" && !picturedYear;
  const activityQuery = useInfiniteQuery<AlbumActivityResponse>({
    queryKey: ["album-activity", familySlug],
    queryFn: ({ pageParam }) =>
      apiFetch<AlbumActivityResponse>(
        `/families/${familySlug}/albums/activity` +
          (pageParam ? `?before=${encodeURIComponent(pageParam as string)}` : ""),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: showActivity,
  });

  // Scroll-to-top visibility
  useEffect(() => {
    function onScroll() {
      setShowScrollTop(window.scrollY > 600);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Pull-to-refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["posts", familySlug] }),
      queryClient.invalidateQueries({ queryKey: ["album-activity", familySlug] }),
    ]);
    setRefreshing(false);
  }, [queryClient, familySlug]);

  const allPosts = query.data?.pages.flatMap((p) => p.posts) ?? [];

  // Album cards only go as far back as the posts loaded so far, so "Load
  // more" keeps both in step. Once every post is loaded, show them all.
  const activity = showActivity
    ? (activityQuery.data?.pages.flatMap((p) => p.items) ?? [])
    : [];
  const postCutoff =
    query.hasNextPage && allPosts.length > 0
      ? allPosts[allPosts.length - 1]!.createdAt
      : null;
  const oldestActivity = activity[activity.length - 1]?.addedAt;
  const needMoreActivity =
    showActivity &&
    activityQuery.hasNextPage &&
    !activityQuery.isFetchingNextPage &&
    (postCutoff === null || (oldestActivity !== undefined && oldestActivity >= postCutoff));
  useEffect(() => {
    if (needMoreActivity) void activityQuery.fetchNextPage();
  }, [needMoreActivity, activityQuery]);

  if (query.isLoading) {
    return (
      <div className="stack-lg">
        <PostSkeleton />
        <PostSkeleton />
        <PostSkeleton />
      </div>
    );
  }
  if (query.error)
    return (
      <p className="text-error">Error: {(query.error as Error).message}</p>
    );
  if (allPosts.length === 0 && activity.length === 0)
    return (
      <p className="text-muted">
        {picturedYear
          ? `No posts pictured from ${picturedYear}.`
          : "No posts yet. Be the first to share something."}
      </p>
    );

  const membership = meQuery.data?.memberships.find(
    (m) => m.familySlug === familySlug,
  );
  const watermarkVars = {
    user:
      meQuery.data?.user.displayName ??
      meQuery.data?.user.email ??
      meQuery.data?.user.phone ??
      "",
    family: membership?.familyName ?? "",
    phone: meQuery.data?.user.phone ?? null,
    email: meQuery.data?.user.email ?? null,
  };

  // Group posts by year (from event_date if set, else created_at). The
  // newest-added feed is one flat group with no heading — its posts come in
  // upload order, so year headings would repeat and jump around.
  const grouped = new Map<number, Post[]>();
  if (sort === "recent") {
    grouped.set(0, allPosts);
  } else {
    for (const post of allPosts) {
      const year = yearOf(post);
      const arr = grouped.get(year) ?? [];
      arr.push(post);
      grouped.set(year, arr);
    }
  }
  const years = [...grouped.keys()].sort((a, b) => b - a);

  // Newest view: posts and album cards in one list, newest first.
  type FeedEntry =
    | { kind: "post"; at: string; post: Post }
    | { kind: "album"; at: string; item: AlbumActivity };
  const feed: FeedEntry[] = [
    ...allPosts.map((post): FeedEntry => ({ kind: "post", at: post.createdAt, post })),
    ...activity
      .filter((item) => postCutoff === null || item.addedAt >= postCutoff)
      .map((item): FeedEntry => ({ kind: "album", at: item.addedAt, item })),
  ].sort((x, y) => (x.at < y.at ? 1 : x.at > y.at ? -1 : 0));

  const renderPost = (post: Post) => (
              <li key={post.id} className="card" style={{ marginBottom: "var(--s-4)", padding: "var(--s-4)" }}>
                {/* Author row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--s-2)",
                    marginBottom: "var(--s-3)",
                  }}
                >
                  <Avatar
                    src={post.author.avatarUrl ?? null}
                    name={post.author.displayName}
                    size={40}
                  />
                  <div style={{ lineHeight: 1.3 }}>
                    <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                      {post.author.displayName ?? "Unknown"}
                    </span>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "var(--c-text-subtle)",
                      }}
                    >
                      Posted {formatDate(post.createdAt)}
                    </div>
                  </div>
                  {post.visibility === "groups" && (
                    <span
                      className="badge"
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.6rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      🔒 Group
                    </span>
                  )}
                </div>

                {/* Title + pictured date */}
                {post.title && (
                  <h2
                    style={{
                      fontFamily: "var(--font-heading, Georgia, serif)",
                      fontSize: "1.4rem",
                      fontWeight: 400,
                      margin: 0,
                      marginBottom: 4,
                      lineHeight: 1.2,
                    }}
                  >
                    {post.title}
                  </h2>
                )}
                {(post.eventDate || post.placeName) && (
                  <div
                    style={{
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "var(--c-text-subtle)",
                      marginBottom: "var(--s-2)",
                      display: "flex",
                      gap: "var(--s-3)",
                      flexWrap: "wrap",
                    }}
                  >
                    {post.eventDate && (
                      <span>Pictured · {formatPicturedDate(post.eventDate, post.eventDatePrecision, APP_LOCALE)}</span>
                    )}
                    {post.placeName && (
                      <Link to="/world" style={{ color: "inherit", textDecoration: "none" }}>
                        📍 {post.placeName}
                      </Link>
                    )}
                  </div>
                )}

                {/* Body */}
                {post.body && (
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      marginBottom: "var(--s-3)",
                      lineHeight: 1.6,
                      fontSize: "0.95rem",
                    }}
                  >
                    {post.body}
                  </p>
                )}

                {/* Media */}
                {post.media.length > 0 && membership && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        post.media.length === 1
                          ? "1fr"
                          : post.media.length === 2
                            ? "1fr 1fr"
                            : "repeat(auto-fit, minmax(150px, 1fr))",
                      gap: "var(--s-2)",
                      marginBottom: "var(--s-3)",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    {post.media.map((m) => (
                      <MediaTile
                        key={m.id}
                        media={m}
                        familySlug={familySlug}
                        role={membership.role}
                        watermarkConfig={membership.watermark}
                        watermarkVars={watermarkVars}
                        maxHeight={post.media.length === 1 ? 640 : 400}
                      />
                    ))}
                  </div>
                )}
                {membership && (
                  <PostMediaStories
                    familySlug={familySlug}
                    media={post.media}
                    role={membership.role}
                    currentUserId={meQuery.data?.user.id}
                  />
                )}

                {/* Action buttons — pill style */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--s-2)",
                    flexWrap: "wrap",
                    paddingTop: "var(--s-2)",
                    borderTop: "1px solid var(--c-border)",
                  }}
                >
                  <Link
                    to={`/post/${post.id}`}
                    className="post-action-btn"
                    style={{ textDecoration: "none" }}
                  >
                    Open
                  </Link>
                  <PostReactions familySlug={familySlug} post={post} />
                  <ShareButtonPill postId={post.id} />
                  {membership && meQuery.data && (
                    <EditPostButton
                      familySlug={familySlug}
                      post={post}
                      role={membership.role}
                      currentUserId={meQuery.data.user.id}
                    />
                  )}
                  {membership && meQuery.data && (
                    <DeletePostButton
                      familySlug={familySlug}
                      post={post}
                      role={membership.role}
                      currentUserId={meQuery.data.user.id}
                    />
                  )}
                </div>

                {/* Inline comments */}
                {membership && (
                  <div style={{ marginTop: "var(--s-3)" }}>
                    <PostComments
                      familySlug={familySlug}
                      postId={post.id}
                      commentCount={post.commentCount ?? 0}
                      currentUserId={meQuery.data?.user.id}
                      isAdmin={can(membership.role, "post:delete:any")}
                      defaultExpanded
                    />
                  </div>
                )}
              </li>
  );

  return (
    <div className="stack-lg">
      {years.map((year) => (
        <div key={year}>
          {sort !== "recent" && (
            <h3
              className="timeline-year-heading"
              style={{
                fontFamily: "var(--font-heading, Georgia, serif)",
                fontStyle: "italic",
                color: "var(--c-accent, var(--c-blue))",
                fontSize: "2.2rem",
                fontWeight: 400,
                margin: 0,
                marginBottom: "var(--s-3)",
                position: "sticky",
                top: 117,
                zIndex: 4,
                background: "var(--c-bg)",
                padding: "var(--s-1) 0",
              }}
            >
              {year}
            </h3>
          )}
          <ul className="list-unstyled">
            {sort === "recent"
              ? feed.map((entry) =>
                  entry.kind === "album" ? (
                    <AlbumActivityCard key={entry.item.id} item={entry.item} />
                  ) : (
                    renderPost(entry.post)
                  ),
                )
              : grouped.get(year)!.map(renderPost)}
          </ul>
        </div>
      ))}

      {/* Load more */}
      {query.hasNextPage && (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          style={{ alignSelf: "center", margin: "0 auto", display: "block" }}
        >
          {query.isFetchingNextPage ? "Loading…" : "Load more posts"}
        </button>
      )}

      {query.isFetchingNextPage && <PostSkeleton />}

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Scroll to top"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 48,
            height: 48,
            minHeight: 48,
            borderRadius: "50%",
            background: "var(--c-blue)",
            color: "#fff",
            border: "none",
            fontSize: "1.3rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            cursor: "pointer",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
}

function DeletePostButton({
  familySlug,
  post,
  role,
  currentUserId,
}: {
  familySlug: string;
  post: Post;
  role: Role;
  currentUserId: string;
}) {
  const queryClient = useQueryClient();
  const isOwn = post.author.id === currentUserId;
  const allowed = isOwn
    ? can(role, "post:delete:own")
    : can(role, "post:delete:any");

  const del = useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch(`/families/${familySlug}/posts/${post.id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
    },
  });

  if (!allowed) return null;

  function onClick() {
    const label = post.title || post.body.slice(0, 40) + "…";
    if (window.confirm(`Delete post "${label}"? This cannot be undone.`)) {
      del.mutate();
    }
  }

  return (
    <button
      type="button"
      className="btn-tertiary"
      onClick={onClick}
      disabled={del.isPending}
      style={{
        padding: "6px 10px",
        fontSize: "0.85rem",
        minHeight: 32,
        color: "var(--c-danger)",
      }}
    >
      {del.isPending ? "Deleting…" : "Delete"}
    </button>
  );
}

function EditPostButton({
  familySlug,
  post,
  role,
  currentUserId,
}: {
  familySlug: string;
  post: Post;
  role: Role;
  currentUserId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(post.title ?? "");
  const [body, setBody] = useState(post.body);
  const [eventDate, setEventDate] = useState(
    eventDateInputValue(post.eventDate, post.eventDatePrecision),
  );

  const isOwn = post.author.id === currentUserId;
  const allowed = isOwn
    ? can(role, "post:edit:own")
    : can(role, "post:edit:any");

  const edit = useMutation({
    mutationFn: () =>
      apiFetch(`/families/${familySlug}/posts/${post.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: title.trim() || null,
          body: body.trim(),
          eventDate: eventDate || null,
        }),
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["post", post.id] });
    },
  });

  if (!allowed) return null;

  if (!editing) {
    return (
      <button
        type="button"
        className="btn-tertiary"
        onClick={() => setEditing(true)}
        style={{ padding: "6px 10px", fontSize: "0.85rem", minHeight: 32 }}
      >
        Edit
      </button>
    );
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (body.trim()) edit.mutate(); }}
        className="card stack"
        style={{ width: "100%", maxWidth: 500 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: "1rem" }}>Edit post</h3>
        <div>
          <label htmlFor="edit-title">Title</label>
          <input id="edit-title" type="text" value={title}
            onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>
        <div>
          <label htmlFor="edit-body">Body *</label>
          <textarea id="edit-body" value={body}
            onChange={(e) => setBody(e.target.value)} required rows={4}
            style={{
              width: "100%", background: "var(--c-bg)", color: "var(--c-text)",
              border: "1px solid var(--c-border-strong)", borderRadius: 4,
              padding: "14px 16px", fontSize: "1rem", fontFamily: "inherit", resize: "vertical",
            }} />
        </div>
        <div>
          <label htmlFor="edit-date">Pictured date</label>
          <input
            id="edit-date"
            type="text"
            inputMode="numeric"
            placeholder="YYYY, YYYY-MM, or YYYY-MM-DD"
            pattern="\d{4}(-\d{2}(-\d{2})?)?"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
          />
        </div>
        {edit.error && <p className="text-error">{edit.error.message}</p>}
        <div style={{ display: "flex", gap: "var(--s-3)" }}>
          <button type="submit" disabled={edit.isPending || !body.trim()}>
            {edit.isPending ? "Saving…" : "Save"}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function ShareButtonPill({ postId }: { postId: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/p/${postId}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      className="post-action-btn"
      onClick={handleCopy}
    >
      {copied ? "Copied!" : "Share"}
    </button>
  );
}
